import React, { useEffect, useRef } from 'react';

/** 点阵网格间距（px） */
const GRID_PX = 21;
/** 高亮层边长（px），遮罩圆半径为其一半；须为 GRID_PX 的偶数倍（一半也是网格整数倍），
 *  层原点取整到网格后亮点才能与基础点阵逐像素对齐 */
const LAYER_PX = 252;
/** 尾迹层数（不含头层） */
const TRAIL_COUNT = 3;
/** 头层朝鼠标的插值系数（0~1，越大越紧跟） */
const HEAD_EASE = 0.32;
/** 尾迹层相对前一层的插值衰减（逐层更慢，形成拖尾） */
const TRAIL_EASE_DECAY = 0.6;
/** 光斑组淡入淡出插值系数 */
const FADE_EASE = 0.08;
/** 基础点阵中心透明度（配合遮罩向视口边缘衰减） */
const BASE_CENTER_ALPHA = 0.35;
/** 基础点阵边缘透明度 */
const BASE_EDGE_ALPHA = 0.15;
/** 基础点阵颜色：中心区域白点 */
const BASE_DOT_COLOR = `rgba(255, 255, 255, ${BASE_CENTER_ALPHA})`;
/** 基础层遮罩：内接椭圆 45% 半径内全亮，向四边中点线性衰减到边缘值；
 *  遮罩为乘法叠加，边缘 alpha=边缘点透明度/中心点透明度，使边缘点恰为 BASE_EDGE_ALPHA */
const BASE_LAYER_MASK = `radial-gradient(ellipse 50% 50% at center, #000 0% 45%, rgba(0, 0, 0, ${(
  BASE_EDGE_ALPHA / BASE_CENTER_ALPHA
).toFixed(4)}) 100%)`;
/** 高亮点阵颜色：光斑内被点亮的白点 */
const HIGHLIGHT_DOT_COLOR = 'rgba(255, 255, 255, 0.8)';

const LAYER_HALF_PX = LAYER_PX / 2;

/** 光斑遮罩：中心 25% 半径内全亮，随后按近似 smoothstep 曲线柔和衰减，
 *  在到达层边界前已衰减为 0，避免光斑出现生硬的圆形边界 */
const HIGHLIGHT_MASK = `radial-gradient(circle ${LAYER_HALF_PX}px at center, #000 0% 25%, rgba(0, 0, 0, 0.9) 40%, rgba(0, 0, 0, 0.65) 55%, rgba(0, 0, 0, 0.35) 70%, rgba(0, 0, 0, 0.1) 85%, transparent 100%)`;

/** 底部渐隐带高度：点阵在接近底部边界（如「图像精选」标题栏）前逐渐淡出，避免硬切边缘 */
const BOTTOM_FADE_PX = 160;
/** 底部边界越过量：点阵延伸到边界元素（标题栏）底部再往下 150px */
const BOUNDARY_OVERSHOOT_PX = 150;
/** 0.6px 半径圆点 + 0.7px 截止：与 oiioii 一致的高密度细点 */
const dotPattern = (color: string) =>
  `radial-gradient(circle at center, ${color} 0 0.6px, transparent 0.7px)`;

interface DotMatrixBackgroundProps {
  /** 可选底部边界元素：点阵（含光斑拖尾）延伸到该元素在页面中的布局底部再往下一段，
   *  随布局实时跟随；不传则铺满整个首页内容高度 */
  bottomBoundaryRef?: React.RefObject<HTMLElement | null>;
}

/**
 * 首页点阵背景（交互参考 oiioii.tv 首页）：
 * - 定位：absolute 锚定首页内容区，固定在页面上、随页面滚动（非视口固定层）；
 * - 基础层：radial-gradient 平铺的静态点阵铺满内容区，径向遮罩控制中心亮、向边缘衰减；
 * - 光斑层：头层 + 尾迹层用同款点阵的更亮版本，叠加圆形渐变遮罩形成
 *   「中心亮、边缘淡」光斑；rAF 链式插值（每层朝前一层缓动）产生点亮拖尾；
 * - 光斑坐标按网格取整，保证亮点与基础点阵逐像素对齐（真正的"点亮"）；
 * - 仅响应鼠标（pointerType==='mouse'）；prefers-reduced-motion 降级为纯静态；
 *   全部淡出后自动停帧，不空转 rAF。
 */
export const DotMatrixBackground: React.FC<DotMatrixBackgroundProps> = ({ bottomBoundaryRef }) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const groupRef = useRef<HTMLDivElement | null>(null);
  const layerRefs = useRef<Array<HTMLDivElement | null>>([]);

  /* 底部边界跟随（文档布局坐标）：点阵已固定在页面上随页面滚动（absolute），
     容器高度钳到 boundary（首页「图像精选」TAB 标题栏）的布局底部再往下 BOUNDARY_OVERSHOOT_PX px。
     用 offsetParent 链累加布局位置——sticky 吸顶只改视觉位置、不改布局位置（offsetTop），
     故无需监听 scroll，仅布局尺寸变化（RO/resize）时重算 */
  useEffect(() => {
    const root = rootRef.current;
    const boundary = bottomBoundaryRef?.current;
    if (!root || !boundary) return;
    const update = () => {
      const rootParent = root.offsetParent as HTMLElement | null;
      let top = 0;
      let el: HTMLElement | null = boundary;
      while (el && el !== rootParent) {
        top += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      const bottom = top + boundary.offsetHeight + BOUNDARY_OVERSHOOT_PX;
      const parentH = rootParent ? rootParent.offsetHeight : bottom;
      root.style.height = `${Math.min(Math.max(bottom, 0), parentH)}px`;
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(boundary);
    // 边界上方内容（图片加载、断点重排等）也会改变其位置，一并观测
    ro.observe(document.body);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [bottomBoundaryRef]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const layerCount = TRAIL_COUNT + 1;
    const target = { x: -9999, y: -9999 };
    const cur = Array.from({ length: layerCount }, () => ({ x: -9999, y: -9999 }));
    let active = false;
    let opacity = 0;
    let hasTarget = false;
    let rafId = 0;

    const quantize = (v: number) => Math.round(v / GRID_PX) * GRID_PX;

    const tick = () => {
      if (hasTarget) {
        cur[0].x += (target.x - cur[0].x) * HEAD_EASE;
        cur[0].y += (target.y - cur[0].y) * HEAD_EASE;
        for (let i = 1; i < layerCount; i++) {
          const ease = HEAD_EASE * Math.pow(TRAIL_EASE_DECAY, i);
          cur[i].x += (cur[i - 1].x - cur[i].x) * ease;
          cur[i].y += (cur[i - 1].y - cur[i].y) * ease;
        }
        const layers = layerRefs.current;
        for (let i = 0; i < layerCount; i++) {
          const el = layers[i];
          if (!el) continue;
          const tx = quantize(cur[i].x) - LAYER_HALF_PX;
          const ty = quantize(cur[i].y) - LAYER_HALF_PX;
          el.style.transform = `translate3d(${tx}px, ${ty}px, 0)`;
        }
      }
      opacity += ((active ? 1 : 0) - opacity) * FADE_EASE;
      const group = groupRef.current;
      if (group) group.style.opacity = opacity < 0.002 ? '0' : opacity.toFixed(3);
      // 完全淡出后停帧，等下次 pointermove 再唤醒
      if (!active && opacity < 0.002) { rafId = 0; return; }
      rafId = requestAnimationFrame(tick);
    };

    const startLoop = () => { if (!rafId) rafId = requestAnimationFrame(tick); };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      // 容器为文档定位（随页面滚动、含侧边栏偏移），鼠标视口坐标需换算为容器内坐标
      const root = rootRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      target.x = e.clientX - rect.left;
      target.y = e.clientY - rect.top;
      if (!hasTarget) {
        hasTarget = true;
        // 首次出现直接对齐鼠标，避免从屏幕外飞入的拖影
        for (const p of cur) { p.x = target.x; p.y = target.y; }
      }
      if (!active) { active = true; startLoop(); }
    };
    const onPointerLeave = () => { active = false; startLoop(); };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', onPointerLeave);
    startLoop();

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('mouseleave', onPointerLeave);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="pointer-events-none absolute inset-x-0 top-0 z-0 overflow-hidden"
      style={{
        // 点阵固定在页面上、随页面滚动（absolute 锚定首页内容区），高度由边界 effect 动态钳制；
        // 无边界时铺满整个首页内容高度
        height: '100%',
        // 底部渐隐 mask 作用于整容器（基础点阵 + 光斑拖尾一并淡出），渐隐带贴着动态底部边界
        WebkitMaskImage: `linear-gradient(to bottom, #000 calc(100% - ${BOTTOM_FADE_PX}px), transparent 100%)`,
        maskImage: `linear-gradient(to bottom, #000 calc(100% - ${BOTTOM_FADE_PX}px), transparent 100%)`,
      }}
      aria-hidden="true"
    >
      {/* 基础点阵：静态白点铺满视口，中心亮、向边缘衰减 */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: dotPattern(BASE_DOT_COLOR),
          backgroundSize: `${GRID_PX}px ${GRID_PX}px`,
          WebkitMaskImage: BASE_LAYER_MASK,
          maskImage: BASE_LAYER_MASK,
        }}
      />
      {/* 光斑层组：头层 + 尾迹层，透明度整体缓入缓出 */}
      <div ref={groupRef} style={{ opacity: 0 }}>
        {Array.from({ length: TRAIL_COUNT + 1 }, (_, i) => (
          <div
            key={i}
            ref={(el) => { layerRefs.current[i] = el; }}
            className="absolute left-0 top-0"
            style={{
              width: LAYER_PX,
              height: LAYER_PX,
              backgroundImage: dotPattern(HIGHLIGHT_DOT_COLOR),
              backgroundSize: `${GRID_PX}px ${GRID_PX}px`,
              WebkitMaskImage: HIGHLIGHT_MASK,
              maskImage: HIGHLIGHT_MASK,
              transform: 'translate3d(-9999px, -9999px, 0)',
              willChange: 'transform',
              contain: 'strict',
            }}
          />
        ))}
      </div>
    </div>
  );
};
